import React, { useEffect, useState } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { swarmApi } from '../api';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import SecurityIcon from '@mui/icons-material/Security';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ScienceIcon from '@mui/icons-material/Science';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import HubIcon from '@mui/icons-material/Hub';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import Badge from '@mui/material/Badge';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Intent Console', icon: <ChatIcon />, path: '/intent', badgeKey: 'intent' },
  { text: 'Services', icon: <MiscellaneousServicesIcon />, path: '/services' },
  { text: 'Certificates', icon: <SecurityIcon />, path: '/certificates' },
  { text: 'Monitoring', icon: <MonitorHeartIcon />, path: '/monitoring' },
  { text: 'Tests', icon: <ScienceIcon />, path: '/tests' },
  { text: 'AI Governance', icon: <SmartToyIcon />, path: '/ai', badgeKey: 'ai' },
  { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { text: 'Networks', icon: <HubIcon />, path: '/networks' },
  { text: 'Containers', icon: <ViewInArIcon />, path: '/containers' },
  { text: 'Cleanup', icon: <CleaningServicesIcon />, path: '/cleanup' },
  { text: 'Swarm Nodes', icon: <HubIcon />, path: '/nodes' },
];

const adminItems = [
  { text: 'Domain Routing', icon: <LanguageIcon />, path: '/domains' },
  { text: 'Policy', icon: <SecurityIcon />, path: '/policy' },
  { text: 'Secrets', icon: <SecurityIcon />, path: '/secrets' },
  { text: 'Users', icon: <PeopleIcon />, path: '/users' },
  { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit' },
];

function Sidebar({ open, user, onClose, observabilityStatus }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const kibanaOnline = observabilityStatus?.kibanaOnline;
  const [pending, setPending] = useState({ awaitingApproval: 0, recommendationsPending: 0, totalAttention: 0 });

  useEffect(() => {
    if (!user) return;
    const load = () => {
      swarmApi.getIntentPendingCount()
        .then(res => setPending(res.data || {}))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const badgeFor = (key) => {
    if (key === 'intent') return pending.awaitingApproval || 0;
    if (key === 'ai') return pending.recommendationsPending || 0;
    return 0;
  };

  const drawerContent = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">NAVIGATION</Typography>
      </Box>
      <List>
        {menuItems.map((item) => {
          const count = badgeFor(item.badgeKey);
          return (
          <ListItem key={item.text} disablePadding>
            <ListItemButton component={Link} to={item.path} selected={location.pathname === item.path} onClick={isMobile ? onClose : undefined}>
              <ListItemIcon>
                {count > 0 ? (
                  <Badge badgeContent={count} color="warning">{item.icon}</Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
          );
        })}
        {/* Kibana Logs External Link */}
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={kibanaOnline ? "/kibana" : "#"}
            target={kibanaOnline ? "_blank" : undefined}
            rel="noopener noreferrer"
            disabled={!kibanaOnline}
            sx={{
              opacity: kibanaOnline ? 1 : 0.6,
            }}
          >
            <ListItemIcon><OpenInNewIcon /></ListItemIcon>
            <ListItemText
              primary="Kibana Logs"
              secondary={!kibanaOnline ? "Offline" : undefined}
              secondaryTypographyProps={{ style: { color: 'orange', fontSize: '10px' } }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      {user?.role === 'admin' && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">ADMIN</Typography>
          </Box>
          <List>
            {adminItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton component={Link} to={item.path} selected={location.pathname === item.path} onClick={isMobile ? onClose : undefined}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </>
  );

  if (!open) return null;

  // Mobile: temporary overlay drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', mt: 8 },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', mt: 8 },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Sidebar;
